import { action, makeObservable, observable, runInAction } from 'mobx';

import { inject, injectable } from '@servicetitan/react-ioc';

import {
    ApiGroup,
    ApiScopeGroup,
    APP_API_TOKEN,
    AppReviewStatus,
    AppScopesVersionModel,
    DeveloperType,
    IAppApi,
} from '../../api/app.api';
import { SiteMode, environment } from '../../api';

export interface AppGridItem {
    id: number;
    appName: string;
    apiGroupDisplayName: string;
    currentScopesVersion: AppScopesVersionModel;
    deleted: boolean;
    supportsWebhookEvents: boolean;
    reviewStatus: AppReviewStatus;
}

@injectable()
export class AppListPageStore {
    @observable.ref appGridItems: AppGridItem[] = [];
    @observable showApiCategories = false;
    @observable showReviewStatus = false;
    @observable loading = false;
    @observable developerType = DeveloperType.ThirdParty;
    @observable thirdPartyGateDialogVisible = false;

    constructor(@inject(APP_API_TOKEN) private readonly appApi: IAppApi) {
        makeObservable(this);
    }

    @action
    showThirdPartyGateDialog(): void {
        this.thirdPartyGateDialogVisible = true;
    }

    @action
    hideThirdPartyGateDialog(): void {
        this.thirdPartyGateDialogVisible = false;
    }

    @action
    async load(deleted: boolean): Promise<void> {
        this.loading = true;
        try {
            const [config, apps] = await Promise.all([
                this.appApi.getApiConfiguration(),
                this.appApi.getApps(),
            ]);
            runInAction(() => {
                this.developerType = config.developerType;
                this.appGridItems = apps
                    .filter(app => app.deleted === deleted)
                    .map(app => ({
                        id: app.id,
                        appName: app.appName,
                        apiGroupDisplayName: config.apiGroups.find(
                            apiGroup => apiGroup.name === app.apiGroupName,
                        )!.displayName,
                        currentScopesVersion: app.currentScopesVersion,
                        deleted: app.deleted,
                        supportsWebhookEvents: this.doesApiGroupSupportWebhookEvents(
                            config.apiGroups.find(apiGroup => apiGroup.name === app.apiGroupName)!,
                        ),
                        reviewStatus: app.reviewStatus,
                    }));
                this.showApiCategories = config.apiGroups.length > 1;
                this.showReviewStatus =
                    environment.site.mode === SiteMode.TenantApis &&
                    (config.developerType === DeveloperType.ThirdParty ||
                        (config.developerType === DeveloperType.Customer &&
                            !config.autoApproveCustomerApps));
                this.loading = false;
            });
        } catch {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    async handleThirdPartyGateDialogContinue(isOwnOrg: boolean): Promise<void> {
        if (isOwnOrg) {
            await this.appApi.acceptNewAppTermsOfUse();
        }
        this.hideThirdPartyGateDialog();
    }

    private doesApiGroupSupportWebhookEvents(apiGroup: ApiGroup): boolean {
        return apiGroup.scopeGroups.some((apiScopeGroup: ApiScopeGroup) => {
            return apiScopeGroup.webhookEvents.length > 0;
        });
    }
}
